import React, { memo } from "react";
import { motion } from "framer-motion";
import { roundResultVariants } from "../animations/variants";
import { UI_STRINGS } from "../strings/uiStrings";
import { GAME_CONFIG } from "../config/gameConfig";

interface RoundResultOverlayProps {
  winnerName: string;
  winnerId: string;
  player1Id: string;
  player2Id: string;
  roundHistory: string[];
}

/**
 * Overlay shown after each round's collision resolves.
 * Displays the winner's name and updated win scores.
 */
function RoundResultOverlayComponent({
  winnerName,
  winnerId,
  player1Id,
  player2Id,
  roundHistory,
}: RoundResultOverlayProps): React.ReactElement {
  const isLocalWin = winnerId === player1Id;
  const accentColor = isLocalWin
    ? GAME_CONFIG.colors.you
    : GAME_CONFIG.colors.enemy;

  return (
    <div
      className="absolute inset-0 flex items-center justify-center pointer-events-none"
      style={{ zIndex: 45 }}
    >
      <motion.div
        variants={roundResultVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        style={{ willChange: "transform, opacity" }}
        className="flex flex-col items-center gap-1"
      >
        {/* Winner name */}
        <div
          style={{
            fontFamily: "var(--font-game)",
            fontSize: "3.6rem",
            fontWeight: 900,
            fontStyle: "italic",
            color: accentColor,
            lineHeight: 1,
            textShadow: `0 0 30px ${accentColor}80, 0 4px 12px rgba(0,0,0,0.9)`,
            letterSpacing: "-0.02em",
            userSelect: "none",
            textTransform: "uppercase",
          }}
        >
          {winnerName}
        </div>

        {/* "WINS THE ROUND!" */}
        <div
          style={{
            fontFamily: "var(--font-game)",
            fontSize: "1.6rem",
            fontWeight: 700,
            fontStyle: "italic",
            color: "white",
            textShadow: "0 2px 8px rgba(0,0,0,0.9)",
            letterSpacing: "0.05em",
            userSelect: "none",
          }}
        >
          {UI_STRINGS.roundResult.winsRound}
        </div>

        {/* Score dots */}
        <div className="flex items-center gap-5 mt-3">
          <WinDots roundHistory={roundHistory} playerId={player1Id} />
          <span
            style={{
              fontFamily: "var(--font-game)",
              fontSize: "1rem",
              color: "rgba(255,255,255,0.5)",
              fontWeight: 700,
            }}
          >
            {UI_STRINGS.score.vs}
          </span>
          <WinDots roundHistory={roundHistory} playerId={player2Id} reverse />
        </div>
      </motion.div>
    </div>
  );
}

export const RoundResultOverlay = memo(RoundResultOverlayComponent);

// ─── Win dots ─────────────────────────────────────────────────────────────────

interface WinDotsProps {
  roundHistory: string[];
  playerId: string;
  reverse?: boolean;
}

function WinDots({
  roundHistory,
  playerId,
  reverse = false,
}: WinDotsProps): React.ReactElement {
  const total = GAME_CONFIG.maxRounds;
  const dots = Array.from({ length: total }, (_, i) => {
    const w = roundHistory[i];
    if (w === undefined) return "empty" as const;
    return w === playerId ? ("win" as const) : ("loss" as const);
  });
  const ordered = reverse ? [...dots].reverse() : dots;

  return (
    <div className="flex items-center gap-2">
      {ordered.map((state, i) => {
        const color =
          state === "win"
            ? GAME_CONFIG.colors.roundWin
            : state === "loss"
              ? GAME_CONFIG.colors.enemy
              : null;
        return (
          <div
            key={i}
            style={{
              width: 14,
              height: 14,
              borderRadius: "50%",
              background: color ?? "rgba(255,255,255,0.15)",
              border: `2px solid ${color ?? "rgba(255,255,255,0.25)"}`,
              boxShadow: color ? `0 0 8px ${color}` : "none",
              transition: "background 0.2s",
            }}
          />
        );
      })}
    </div>
  );
}
